import {
  Body,
  Controller,
  Delete,
  Get,
  Param,
  Patch,
  Query,
  Req,
  UseGuards,
} from '@nestjs/common';
import {
  ApproveMerchantRequest,
  MerchantListFilter,
  MerchantListSort,
  RejectMerchantRequest,
  UpdateMerchantSettingsRequest,
} from '@cart/shared-types';
import { AuthGuard, GuardedRequest } from '../auth/auth.guard';
import { DemoModeGuard } from '../auth/demo-mode.guard';
import { PlatformService } from './platform.service';

@Controller('platform/merchants')
@UseGuards(AuthGuard, DemoModeGuard)
export class MerchantsController {
  constructor(private readonly platformService: PlatformService) {}

  /**
   * Paged, searchable, sortable merchant list.
   *
   * Query params arrive as strings; the service does the coercion and clamping
   * so an odd query string degrades to the default rather than 500-ing.
   */
  @Get()
  listMerchants(
    @Query('page') page?: string,
    @Query('limit') limit?: string,
    @Query('search') search?: string,
    @Query('filter') filter?: MerchantListFilter,
    @Query('sort') sort?: MerchantListSort,
    @Query('dir') dir?: 'asc' | 'desc',
  ) {
    return this.platformService.listMerchants({
      page: page ? Number(page) : undefined,
      limit: limit ? Number(limit) : undefined,
      search,
      filter,
      sort,
      dir,
    });
  }

  @Get(':id')
  getMerchant(@Param('id') id: string) {
    return this.platformService.getMerchant(id);
  }

  @Patch(':id/suspend')
  suspendMerchant(
    @Param('id') id: string,
    @Body() body: { reason?: string },
    @Req() req: GuardedRequest
  ) {
    return this.platformService.suspendMerchant(id, body.reason ?? '', req.user.id);
  }

  @Patch(':id/activate')
  activateMerchant(@Param('id') id: string, @Req() req: GuardedRequest) {
    return this.platformService.activateMerchant(id, req.user.id);
  }

  @Patch(':id/approve')
  approveMerchant(
    @Param('id') id: string,
    @Body() body: ApproveMerchantRequest,
    @Req() req: GuardedRequest
  ) {
    return this.platformService.approveMerchant(id, body, req.user.id);
  }

  @Patch(':id/reject')
  rejectMerchant(
    @Param('id') id: string,
    @Body() body: RejectMerchantRequest,
    @Req() req: GuardedRequest
  ) {
    return this.platformService.rejectMerchant(id, body.reason ?? '', req.user.id);
  }

  /**
   * Closes a merchant account. Soft — see PlatformService.deleteMerchant.
   *
   * DELETE rather than PATCH because that is what it means to the caller, even
   * though nothing is physically removed.
   */
  @Delete(':id')
  deleteMerchant(@Param('id') id: string, @Req() req: GuardedRequest) {
    return this.platformService.deleteMerchant(id, req.user.id);
  }

  @Patch(':id/restore')
  restoreMerchant(@Param('id') id: string, @Req() req: GuardedRequest) {
    return this.platformService.restoreMerchant(id, req.user.id);
  }

  @Patch(':id/settings')
  updateSettings(
    @Param('id') id: string,
    @Body() body: UpdateMerchantSettingsRequest,
    @Req() req: GuardedRequest,
  ) {
    return this.platformService.updateMerchantSettings(id, body, req.user.id);
  }

  @Get('stores/:storeId')
  getStore(@Param('storeId') storeId: string) {
    return this.platformService.getStore(storeId);
  }

  @Patch('stores/:storeId/activate')
  activateStore(@Param('storeId') storeId: string, @Req() req: GuardedRequest) {
    return this.platformService.toggleStore(storeId, true, req.user.id);
  }

  @Patch('stores/:storeId/deactivate')
  deactivateStore(@Param('storeId') storeId: string, @Req() req: GuardedRequest) {
    return this.platformService.toggleStore(storeId, false, req.user.id);
  }
}
